import * as React from 'react';
import * as _ from 'lodash';
import { Dispatch, connect } from 'react-redux';
import { IApplicationState, AppActionCreators, IAppState, IMicrositeResponse, MicrositeActionCreators, ICommunicationResponse } from '@nkr/store/index';
import { IBaseResponse, IMicrositeRecipient } from '@nkr/models/index';
import { CustomSiteStatus } from '@nkr/enums/microsite-recipient-status';
import { Alert, Button, Col, Panel, Row } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { AxiosError } from 'axios';
import ReactHtmlParser from 'react-html-parser';
import MicrositeHistory from './microsite-history';
import Communication from './coach/communication';

interface IProps {
    micrositeId: number,
    centerId: number,
    isAdmin: boolean,
    previousUrl: string,
    isCoachView: boolean
}

interface IStateProps {
    app: IAppState
}

interface IDispatchProps {
    showLoading: (message: string) => Promise<void>,
    hideLoading: () => void,
    getMicrosite: (id: number, forCoach: boolean) => Promise<IMicrositeResponse>,
    approve: (id: number, editKey: string) => Promise<IBaseResponse>,
    sendNote: (micrositeRecipientId: number, note: string) => Promise<ICommunicationResponse>,
    replyToNote: (noteId: number, reply: string) => Promise<ICommunicationResponse>
}

type TProps = IProps & IStateProps & IDispatchProps;

interface IState {
    recipient: IMicrositeRecipient,
    previousLocation: string
}

class MicrositeApprove extends React.Component<TProps, IState> {
    readonly state: IState = {
        recipient: null,
        previousLocation: ''
    }

    componentDidMount = () => {
        let previousLocation = this.props.isAdmin ? `/admin/microsite/${this.props.previousUrl}` : `/center/${this.props.centerId}/microsite/${this.props.previousUrl}`;

        if (this.props.previousUrl == 'microsite/coach/approval') {
            previousLocation = 'microsite/coach/approval';
        }

        this.props.showLoading('Getting the microsite, one moment please...').then(() => {
            this.props.getMicrosite(this.props.micrositeId, false)
                .then(response => {
                    if (response.data.success) {
                        this.setState({
                            recipient: response.data.results,
                            previousLocation
                        });
                    }

                    this.props.hideLoading();
                })
                .catch((error: AxiosError) => {
                    this.props.hideLoading();
                });
        });
    }

    handleApprove = () => {
        let { id, editKey } = this.state.recipient;

        this.props.showLoading('Approving microsite, one moment please...').then(() => {
            this.props.approve(id, editKey)
                .then(response => {
                    if (response.data.success) {
                        toast.success('Microsite approved successfully, going back to previous page', {
                            onClose: () => {
                                window.location.href = this.state.previousLocation;
                            }
                        });
                    }
                })
                .catch((error: AxiosError) => {
                    toast.error('Unable to approve the microsite, the following error has been recorded: ' + error.message);
                })
                .finally(()=> {
                    this.props.hideLoading();
                });
        });
    }

    handleCancel = () => {
        window.location.href = this.state.previousLocation;
    }

    handleSendNote = (text: string, isReply: boolean, itemId: number) => {
            let { id } = this.state.recipient;
    
            let message = isReply ? `Sending your reply to ${this.state.recipient.fullName}, one moment please` : `Sending a note to ${this.state.recipient.fullName}, one moment please...`;
            let request = isReply ? this.props.replyToNote(itemId, text) : this.props.sendNote(id, text);
    
            this.props.showLoading(message).then(() => {
                request.then(response => {
                    this.props.hideLoading();
    
                    if (response.data.success) {
                        let comms = response.data.results;
    
                        let recipient = _.cloneDeep(this.state.recipient);
                        recipient.comms = comms;
        
                        this.setState({
                            recipient
                        }, () => {
                            toast.success(isReply ? 'Reply sent successfully' : 'New note sent successfully');
                        });
                    }
                })
            })
    }

    render = () => {
        if (this.state.recipient == null)
            return <div>One moment please...</div>;

        const { micrositeType, customSiteStatusId, submissionLastIssue, fullName, patientStoryPending, patientWhyPending, imagesPending, logs, centerId } = this.state.recipient;

        const hasCoachApproval = logs.some(l => l.micrositeRecipientStatus?.includes('CustomSiteCoachApproved'));
        const nextCenterAction = hasCoachApproval ? 'update the live site.' : 'send to NKR for final approval.';

        const forChanges = _.orderBy(logs, l => l.logDateTime, ['desc']).find(l => l.logEntry.includes('Custom Site - Active')) != null;
        
        return (
            <>
                <div className='container'>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 20, marginBottom: 20 }}>
                        <h1 style={{ margin: 0 }}>Mark as Approved</h1>
                        <div className="pull-right">
                            <Button bsStyle='link' onClick={this.handleCancel}>Return to Previous List</Button>
                        </div>
                    </div>

                    <div className='row'>
                        <div className='col-xs-12'>
                            {customSiteStatusId == CustomSiteStatus.PendCoachApproval && centerId > 0 && (
                                <Alert bsStyle='info' bsSize='sm'>
                                    Please confirm that you wish to approve this patient's microsite and send to the center for final approval.
                                </Alert>
                            )}

                            {customSiteStatusId == CustomSiteStatus.PendCoachApproval && centerId == 0 && (
                                <Alert bsStyle='info' bsSize='sm'>
                                    Please confirm that you wish to approve this patient's microsite and send to NKR for final approval.
                                </Alert>
                            )}

                            {(customSiteStatusId == CustomSiteStatus.PendCenterApproval || customSiteStatusId == CustomSiteStatus.PendCtrDeclinedFixable) && (
                                <Alert bsStyle='info' bsSize='sm'>
                                    Please confirm that you wish to approve this patient's microsite and {nextCenterAction}
                                </Alert>
                            )}

                            {(customSiteStatusId == CustomSiteStatus.PendCenterApproval) && (
                                <Alert bsStyle='info' bsSize='sm'>
                                    Please confirm that you wish to approve this patient's custom microsite.
                                </Alert>
                            )}

                            {customSiteStatusId == CustomSiteStatus.PendNkrApproval && forChanges && (
                                <Alert bsStyle='info' bsSize='sm'>
                                    Please confirm that you wish to approve this patient's microsite changes and update the live site.
                                </Alert>
                            )}

                            {customSiteStatusId == CustomSiteStatus.PendNkrApproval && !forChanges && (
                                <Alert bsStyle='info' bsSize='sm'>
                                    Please confirm that you wish to approve this patient's custom microsite.
                                </Alert>
                            )}
                        </div>
                    </div>

                    {submissionLastIssue != '' && (
                        <Row>
                            <Col xs={12}>
                                <MicrositeHistory
                                    name={fullName}
                                    logs={logs}
                                    isExpanded={false}
                                />
                            </Col>
                        </Row>
                    )}

                    <Row>
                        <Col md={this.props.isCoachView ? 6 : 12} >
                            <Panel>
                                <Panel.Heading>Patient's Story</Panel.Heading>
                                <Panel.Body>
                                    {ReactHtmlParser(patientStoryPending.replace(/(\n)+/g, '<br />'))}
                                </Panel.Body>
                            </Panel>
                            <Panel>
                                <Panel.Heading>Patient's "Why they need a {micrositeType}"</Panel.Heading>
                                <Panel.Body>
                                    {ReactHtmlParser(patientWhyPending.replace(/(\n)+/g, '<br />'))}
                                </Panel.Body>
                            </Panel>
                        </Col>

                        {this.props.isCoachView && 
                            <Col md={6}>
                                <Communication
                                    list={this.state.recipient.comms}
                                    recipientName={fullName}
                                    recipientActive={true}
                                    onSendNote={this.handleSendNote}
                                />
                            </Col>
                        }
                    </Row>
                    
                    <Row>
                        <Col md={12}>
                            <Panel>
                                <Panel.Heading>Images</Panel.Heading>
                                <Panel.Body>
                                    {imagesPending.map((image, index) =>
                                        <div>
                                            <div style={{ width: 215, height: 215 }}><img src={`${__NKR_ORG_URL__}/microsite/image?filename=${image.thumbnail}`} style={{ width: 200, height: '100%' }} /></div>
                                            <div className='text-center' style={{ width: 215 }}>{image.caption}</div>
                                        </div>
                                    )}
                                </Panel.Body>
                            </Panel>
                        </Col>
                    </Row>
                    
                    <div style={{ textAlign: 'center', paddingBottom: 10 }}>
                        <Button bsStyle='primary' bsSize='lg' onClick={this.handleApprove}>Mark Approved</Button>{' '}
                        <Button name='cancel' onClick={this.handleCancel}>Cancel Approval</Button>
                    </div>
                </div>
            </>
        );
    }
}

const mapStateToProps = (state: IApplicationState): IStateProps => {
    return {
        app: state.app
    };
}

const mapDispatchToProps = (dispatch: Dispatch<any>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            return dispatch(AppActionCreators.hideLoading());
        },
        getMicrosite: (id, forCoach) => {
            return dispatch(MicrositeActionCreators.getMicrosite(id, forCoach));
        },
        approve: (id, editKey) => {
            return dispatch(MicrositeActionCreators.approve(id, editKey));
        },
        sendNote: (micrositeRecipientId: number, note: string) => {
            return dispatch(MicrositeActionCreators.sendNote(micrositeRecipientId, note));
        },
        replyToNote: (noteId: number, reply: string) => {
            return dispatch(MicrositeActionCreators.replyToNote(noteId, reply));
        }
    };
}

export default connect<IStateProps, IDispatchProps, IProps>(
    mapStateToProps,
    mapDispatchToProps)(MicrositeApprove);